/**
 * Melian Dialogue — client-side consent utilities.
 *
 * Responsibilities
 * ----------------
 * 1. Persist granular consent in localStorage (versioned key `md_cookie_consent_v2`).
 * 2. Boot Google Consent Mode v2 with all non-essential signals set to `denied` BEFORE
 *    any tag / pixel could fire.
 * 3. Gate PostHog (already loaded in index.html) via its `opt_out_capturing`
 *    API until analytics consent is granted.
 * 4. Ship each accept/reject action to `POST /api/consent/log` for the audit trail.
 * 5. Fetch region hint from `GET /api/consent/config` so the banner can render the
 *    correct variant (GDPR opt-in vs CCPA Do-Not-Sell vs OTHER).
 *
 * Everything is defensive — never throws in private/incognito browsers, still lets the
 * UI render even if the audit-log call fails.
 */

const STORAGE_KEY = "md_cookie_consent_v2";
const STORAGE_VERSION = 2;
const LEGACY_KEYS = ["md_cookie_consent"]; // wipe the old v1 accept-only key
const SNOOZE_KEY = "md_cookie_snooze_until"; // ISO timestamp; banner stays hidden until then
const SNOOZE_DAYS = 30;

export const DEFAULT_CATEGORIES = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
};

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function _uuid() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return "cid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getStoredConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== STORAGE_VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function saveConsent({ categories, region, source, consent_id }) {
    const record = {
        version: STORAGE_VERSION,
        consent_id: consent_id || getStoredConsent()?.consent_id || _uuid(),
        ts: new Date().toISOString(),
        categories: { ...DEFAULT_CATEGORIES, ...categories, necessary: true },
        region: region || null,
        source: source || "banner",
    };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
        LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
        localStorage.removeItem(SNOOZE_KEY); // any active snooze is superseded by a real decision
    } catch { /* private-mode: session-only */ }
    applyConsentSideEffects(record.categories);
    return record;
}

// ---------------------------------------------------------------------------
// Google Consent Mode v2 — default = denied
// ---------------------------------------------------------------------------
// Runs immediately at module import time (before any consent decision is made).
// Safe to run even when gtag / GTM is not present — we push to window.dataLayer
// so any later-loading Google tag inherits the correct default.
export function initGoogleConsentMode() {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;
    // GCM v2 signals (denied by default until user consents)
    window.gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
        functionality_storage: "denied",
        personalization_storage: "denied",
        security_storage: "granted",   // strictly necessary — always allowed
        wait_for_update: 500,
    });
}

function pushConsentUpdate(categories) {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    const map = (v) => (v ? "granted" : "denied");
    if (window.gtag) {
        window.gtag("consent", "update", {
            ad_storage:              map(categories.marketing),
            ad_user_data:            map(categories.marketing),
            ad_personalization:      map(categories.marketing),
            analytics_storage:       map(categories.analytics),
            functionality_storage:   map(categories.functional),
            personalization_storage: map(categories.functional),
            security_storage:        "granted",
        });
    }
}

// ---------------------------------------------------------------------------
// PostHog — prior blocking via opt_out_capturing (script is already loaded via
// index.html, so we can't defer it, but we can silence it until consent).
// ---------------------------------------------------------------------------
function applyPostHog(categories) {
    if (typeof window === "undefined" || !window.posthog) return;
    try {
        if (categories.analytics) {
            window.posthog.opt_in_capturing();
        } else {
            window.posthog.opt_out_capturing();
        }
    } catch { /* posthog can be a stub before the async script lands */ }
}

// ---------------------------------------------------------------------------
// Public: apply consent to all downstream integrations
// ---------------------------------------------------------------------------
export function applyConsentSideEffects(categories) {
    pushConsentUpdate(categories);
    applyPostHog(categories);
    if (typeof window !== "undefined") {
        window.__mdConsent = { ...categories };
        window.dispatchEvent(new CustomEvent("md-consent-changed", { detail: categories }));
    }
}

// ---------------------------------------------------------------------------
// Boot — run once at app start. Applies stored consent (if any) so PostHog
// respects the user's last decision on every page load.
// ---------------------------------------------------------------------------
export function bootConsent() {
    initGoogleConsentMode();
    const stored = getStoredConsent();
    if (stored?.categories) {
        applyConsentSideEffects(stored.categories);
    } else {
        // No decision yet → keep everything denied.
        applyConsentSideEffects({ ...DEFAULT_CATEGORIES });
    }
    return stored;
}

// ---------------------------------------------------------------------------
// Audit log — fire-and-forget POST. Uses fetch with credentials so the server
// can also derive country from headers.
// ---------------------------------------------------------------------------
export async function logConsent(record) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/consent/log`;
    try {
        await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                // Double-submit CSRF echo — lib/api.js (axios) does this via an
                // interceptor, but this module uses a bare fetch(), so the
                // header must be added explicitly or the backend 403s every
                // consent decision and the audit trail is never written.
                ..._csrfHeader(),
            },
            body: JSON.stringify({
                categories: record.categories,
                region: record.region,
                source: record.source,
                consent_id: record.consent_id,
                tz: Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone,
            }),
            keepalive: true,
        });
    } catch { /* offline — the localStorage record is our source of truth */ }
}

function _csrfHeader() {
    if (typeof document === "undefined") return {};
    const raw = document.cookie || "";
    for (const chunk of raw.split(";")) {
        const [k, ...v] = chunk.trim().split("=");
        if (k === "md_csrf") return { "X-CSRF-Token": decodeURIComponent(v.join("=")) };
    }
    return {};
}

export async function fetchConsentConfig() {
    try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consent/config`, {
            credentials: "include",
        });
        if (!resp.ok) throw new Error(String(resp.status));
        return await resp.json();
    } catch {
        return { region: "OTHER", country: null, requires_opt_in: false, ccpa_visible: false };
    }
}

// Convenience presets used by the banner buttons.
export const ACCEPT_ALL = { necessary: true, functional: true, analytics: true, marketing: true };
export const REJECT_ALL = { necessary: true, functional: false, analytics: false, marketing: false };

// ---------------------------------------------------------------------------
// Snooze — "Save & customise later" defers the decision by SNOOZE_DAYS.
// While snoozed we keep everything at the safe default (denied) but suppress
// the banner UI. Banner reappears automatically once the snooze expires.
// ---------------------------------------------------------------------------
export function getSnoozeUntil() {
    try {
        const raw = localStorage.getItem(SNOOZE_KEY);
        if (!raw) return null;
        const t = Date.parse(raw);
        return Number.isFinite(t) ? new Date(t) : null;
    } catch {
        return null;
    }
}

export function isSnoozed() {
    const until = getSnoozeUntil();
    if (!until) return false;
    if (until.getTime() <= Date.now()) {
        try { localStorage.removeItem(SNOOZE_KEY); } catch { /* ignore */ }
        return false;
    }
    return true;
}

export function snoozeConsent() {
    const until = new Date(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000);
    try {
        localStorage.setItem(SNOOZE_KEY, until.toISOString());
    } catch { /* private-mode: session-only */ }
    return until;
}

export function clearSnooze() {
    try { localStorage.removeItem(SNOOZE_KEY); } catch { /* ignore */ }
}

export const SNOOZE_WINDOW_DAYS = SNOOZE_DAYS;
